/**
 * ATT-11: a simple inventory of physical biometric attendance devices — a registry, not a vendor
 * integration. See `BiometricDevice`'s own doc comment in schema.prisma.
 */

import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import { errorBodySchema, uuidSchema } from '../schemas/common.js';
import { routeMeta } from './meta.js';

const c = initContract();

export const biometricDeviceDtoSchema = z.object({
  id: uuidSchema,
  label: z.string(),
  serialNumber: z.string(),
  locationId: uuidSchema,
  locationName: z.string(),
  isActive: z.boolean(),
  createdAt: z.string().datetime({ offset: true }),
});
export type BiometricDeviceDto = z.infer<typeof biometricDeviceDtoSchema>;

export const biometricDeviceContract = c.router(
  {
    listBiometricDevices: {
      method: 'GET',
      path: '/',
      responses: {
        200: z.object({ devices: z.array(biometricDeviceDtoSchema) }),
        401: errorBodySchema,
        403: errorBodySchema,
      },
      summary: 'List registered biometric attendance devices for this org',
      metadata: routeMeta('biometric_device:manage'),
    },

    createBiometricDevice: {
      method: 'POST',
      path: '/',
      body: z.object({
        label: z.string().min(1).max(120),
        serialNumber: z.string().min(1).max(120),
        locationId: uuidSchema,
      }),
      responses: {
        201: biometricDeviceDtoSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        409: errorBodySchema,
        422: errorBodySchema,
      },
      summary: 'Register a physical biometric device',
      metadata: routeMeta('biometric_device:manage'),
    },

    updateBiometricDevice: {
      method: 'PUT',
      path: '/:deviceId',
      body: z.object({
        label: z.string().min(1).max(120).optional(),
        isActive: z.boolean().optional(),
      }),
      responses: {
        200: biometricDeviceDtoSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary: 'Rename or activate/deactivate a registered biometric device',
      metadata: routeMeta('biometric_device:manage'),
    },
  },
  { pathPrefix: '/biometric-devices' },
);
